/**
 * Maintenance Dashboard
 * Dedicated interface for maintenance staff
 * Features: Work order queue, equipment status, event notifications, Marina chat
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Wrench, 
  AlertCircle,
  Clock,
  CheckCircle,
  Settings,
  Bell,
  MessageSquare,
  RefreshCw,
  Plus,
  Zap,
  Droplets,
  ThermometerSun,
  Lightbulb,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import { MarinaChat } from '@/components/MarinaChat';
import { useStaffAuth } from '@/hooks/useStaffAuth';
import { BACKEND_URL } from '@/lib/backendUrl';


export default function MaintenanceDashboardPage() {
  // Authentication & Authorization
  const { user, loading: authLoading, hasAccess, logout, hotelId: authHotelId } = useStaffAuth(['maintenance', 'manager', 'owner']);
  
  const [loading, setLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState(null);
  const [showMarina, setShowMarina] = useState(false);
  const hotelId = authHotelId || 'hotel_demo_001'; // Use auth context or fallback

  // Auto-refresh every 30 seconds
  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [statusFilter, priorityFilter]);

  const loadData = async () => {
    await Promise.all([
      loadWorkOrders(),
      loadEquipment(),
      loadEvents()
    ]);
  };

  const loadWorkOrders = async () => {
    try {
      const params = new URLSearchParams({ hotel_id: hotelId, limit: '50' });
      if (statusFilter) params.append('status', statusFilter);
      if (priorityFilter) params.append('priority', priorityFilter);

      const response = await fetch(`${BACKEND_URL}/api/maintenance/${hotelId}/work-orders?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setWorkOrders(data.work_orders || []);
        setSummary(data.summary);
      }
    } catch (error) {
      console.error('Failed to load work orders:', error);
      toast.error('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  };

  const loadEquipment = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/maintenance/${hotelId}/equipment`);
      const data = await response.json();
      
      if (data.success) {
        setEquipment(data.equipment || []);
      }
    } catch (error) {
      console.error('Failed to load equipment:', error);
    }
  };

  const loadEvents = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/events/stream?hotel_id=${hotelId}&target_department=maintenance&status=pending&limit=20`
      );
      const data = await response.json();
      
      if (data.success) {
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  };

  const updateWorkOrder = async (workOrderId, updates) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/maintenance/${hotelId}/work-orders/${workOrderId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates)
        }
      );

      const data = await response.json();
      
      if (data.success) {
        toast.success('Work order updated');
        loadData();
      } else {
        toast.error(data.error || 'Failed to update work order');
      }
    } catch (error) {
      console.error('Failed to update work order:', error);
      toast.error('Failed to update work order');
    }
  };

  const completeEvent = async (eventId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/${eventId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          completed_by: 'maintenance_staff',
          result_data: {
            action_taken: 'Task completed from maintenance dashboard',
            timestamp: new Date().toISOString()
          }
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Task completed! Analytics logged.');
        loadEvents();
      }
    } catch (error) {
      console.error('Failed to complete event:', error);
      toast.error('Failed to complete event');
    }
  };

  const getIssueIcon = (issueType) => {
    switch (issueType) {
      case 'plumbing': return Droplets;
      case 'electrical': return Lightbulb;
      case 'hvac': return ThermometerSun;
      case 'appliance': return Zap;
      default: return Wrench;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="lobby-theme min-h-screen p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-16 w-64" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  // Access denied screen
  if (!hasAccess) {
    return (
      <div className="lobby-theme min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              You don't have permission to access the Maintenance Dashboard.
              {user && (
                <span className="block mt-2">
                  Your role: <strong>{user.role}</strong>
                </span>
              )}
            </p>
            <Button onClick={logout} variant="outline" className="w-full">
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="lobby-theme min-h-screen">
      {/* Header */}
      <div className="border-b border-[color:var(--l-line)] bg-[color:var(--l-bg-2)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-[hsl(38_92%_55%)]/10">
                <Wrench className="w-6 h-6 text-[hsl(38_92%_55%)]" />
              </div>
              <div>
                <h1 className="text-3xl heading-font tracking-tight">
                  Maintenance Dashboard
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Work orders • Equipment • Marina alerts
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {user && (
                <div className="hidden md:flex items-center gap-2 mr-3 text-sm">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">{user.name}</span>
                  <Badge variant="secondary" className="capitalize">{user.role}</Badge>
                </div>
              )}
              
              {events.length > 0 && (
                <Badge variant="destructive" className="animate-pulse">
                  <Bell className="w-3 h-3 mr-1" />
                  {events.length} Alert{events.length !== 1 ? 's' : ''}
                </Badge>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={loadData}
                data-testid="refresh-dashboard-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>

              <Button
                onClick={() => setShowMarina(!showMarina)}
                data-testid="toggle-marina-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Marina AI
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all"
              onClick={() => setStatusFilter(null)}
              data-testid="total-orders-card"
            >
              <CardContent className="p-6 text-center">
                <Settings className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                <p className="text-4xl heading-font tabular-nums">{summary.total}</p>
                <p className="text-sm text-muted-foreground mt-1">Total Orders</p>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:shadow-lg transition-all border-destructive/30 bg-destructive/5"
              onClick={() => setPriorityFilter(priorityFilter === 'urgent' ? null : 'urgent')}
              data-testid="urgent-orders-card"
            >
              <CardContent className="p-6 text-center">
                <AlertCircle className="w-6 h-6 text-destructive mx-auto mb-2" />
                <p className="text-4xl heading-font tabular-nums text-destructive">{summary.urgent}</p>
                <p className="text-sm text-muted-foreground mt-1">Urgent</p>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:shadow-lg transition-all border-primary/20"
              onClick={() => setStatusFilter(statusFilter === 'pending' ? null : 'pending')}
              data-testid="pending-orders-card"
            >
              <CardContent className="p-6 text-center">
                <Clock className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-4xl heading-font tabular-nums text-primary">{summary.pending}</p>
                <p className="text-sm text-muted-foreground mt-1">Pending</p>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:shadow-lg transition-all border-[hsl(38_92%_55%)]/20 bg-[hsl(38_92%_55%)]/5"
              onClick={() => setStatusFilter(statusFilter === 'in_progress' ? null : 'in_progress')}
              data-testid="in-progress-orders-card"
            >
              <CardContent className="p-6 text-center">
                <Wrench className="w-6 h-6 text-[hsl(38_92%_55%)] mx-auto mb-2" />
                <p className="text-4xl heading-font tabular-nums text-[hsl(38_92%_55%)]">{summary.in_progress}</p>
                <p className="text-sm text-muted-foreground mt-1">In Progress</p>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:shadow-lg transition-all"
              onClick={() => setStatusFilter(statusFilter === 'completed' ? null : 'completed')}
              data-testid="completed-orders-card"
            >
              <CardContent className="p-6 text-center">
                <CheckCircle className="w-6 h-6 text-[hsl(var(--status-clean))] mx-auto mb-2" />
                <p className="text-4xl heading-font tabular-nums text-[hsl(var(--status-clean))]">{summary.completed}</p>
                <p className="text-sm text-muted-foreground mt-1">Completed</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pending Event Notifications */}
        {events.length > 0 && (
          <div>
            <h2 className="text-xl heading-font mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              New Alerts from Marina
            </h2>
            <div className="space-y-3">
              {events.map((event) => (
                <Alert
                  key={event.event_id}
                  className="border-primary/30 bg-primary/5"
                  data-testid={`event-alert-${event.event_id}`}
                >
                  <Wrench className="w-4 h-4 text-primary" />
                  <AlertDescription>
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="font-medium text-sm">
                          {formatEventType(event.event_type)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {renderEventSummary(event)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => completeEvent(event.event_id)}
                        data-testid={`complete-event-${event.event_id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Done
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          </div>
        )}

        {/* Equipment Status */}
        {equipment.length > 0 && (
          <div>
            <h2 className="text-xl heading-font mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Equipment Status
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {equipment.map((item) => (
                <Card key={item.id} data-testid={`equipment-${item.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-base">{item.name}</CardTitle>
                      <Badge 
                        variant={item.status === 'operational' ? 'default' : 'destructive'}
                        className="capitalize"
                      >
                        {item.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Type:</span>
                        <span className="capitalize">{item.type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Service:</span>
                        <span className="tabular-nums">{item.last_service}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Next Service:</span>
                        <span className="tabular-nums font-medium">{item.next_service}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium">Filters:</span>
          
          <div className="flex gap-2">
            <Badge 
              variant={statusFilter === null ? 'default' : 'outline'} 
              className="cursor-pointer"
              onClick={() => setStatusFilter(null)}
              data-testid="filter-all-status"
            >
              All Status
            </Badge>
            <Badge 
              variant={statusFilter === 'pending' ? 'default' : 'outline'} 
              className="cursor-pointer"
              onClick={() => setStatusFilter(statusFilter === 'pending' ? null : 'pending')}
              data-testid="filter-pending"
            >
              Pending
            </Badge>
            <Badge 
              variant={statusFilter === 'in_progress' ? 'default' : 'outline'} 
              className="cursor-pointer"
              onClick={() => setStatusFilter(statusFilter === 'in_progress' ? null : 'in_progress')}
              data-testid="filter-in-progress"
            >
              In Progress
            </Badge>
            <Badge 
              variant={statusFilter === 'completed' ? 'default' : 'outline'} 
              className="cursor-pointer"
              onClick={() => setStatusFilter(statusFilter === 'completed' ? null : 'completed')}
              data-testid="filter-completed"
            >
              Completed
            </Badge>
          </div>

          <div className="w-px h-6 bg-border" />

          <div className="flex gap-2">
            <Badge 
              variant={priorityFilter === 'urgent' ? 'destructive' : 'outline'} 
              className="cursor-pointer"
              onClick={() => setPriorityFilter(priorityFilter === 'urgent' ? null : 'urgent')}
              data-testid="filter-urgent"
            >
              Urgent Only
            </Badge>
            <Badge 
              variant={priorityFilter === 'high' ? 'default' : 'outline'} 
              className="cursor-pointer"
              onClick={() => setPriorityFilter(priorityFilter === 'high' ? null : 'high')}
              data-testid="filter-high"
            >
              High Priority
            </Badge>
          </div>
        </div>

        {/* Work Orders Grid */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl heading-font flex items-center gap-2">
              <Wrench className="w-5 h-5" />
              Work Orders ({workOrders.length})
            </h2>
          </div>

          {workOrders.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <CheckCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="heading-font text-lg mb-2">No Work Orders</h3>
                <p className="text-sm text-muted-foreground">
                  {statusFilter || priorityFilter 
                    ? 'No work orders match your filters' 
                    : 'All systems operational! 🎉'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {workOrders.map(order => {
                const IssueIcon = getIssueIcon(order.issue_type);
                
                return (
                  <Card 
                    key={order.work_order_id}
                    className={`hover:shadow-lg transition-all ${
                      order.priority === 'urgent' 
                        ? 'border-destructive/30 bg-destructive/5' 
                        : order.priority === 'high'
                        ? 'border-[hsl(38_92%_55%)]/30 bg-[hsl(38_92%_55%)]/5'
                        : ''
                    }`}
                    data-testid={`work-order-${order.work_order_id}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="p-2 rounded-lg bg-muted">
                            <IssueIcon className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <CardTitle className="text-base leading-tight">
                              {order.title}
                            </CardTitle>
                            <p className="text-xs text-muted-foreground mt-1">
                              {order.room_number ? `Room ${order.room_number}` : order.location}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          <Badge 
                            variant={
                              order.priority === 'urgent' ? 'destructive' : 
                              order.priority === 'high' ? 'warning' : 
                              'outline'
                            }
                            className="capitalize"
                          >
                            {order.priority}
                          </Badge>
                          <Badge 
                            variant="outline"
                            className="capitalize text-xs"
                          >
                            {order.status.replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-3">
                      <p className="text-sm text-muted-foreground">
                        {order.description}
                      </p>

                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div className="p-2 rounded-lg bg-muted/50">
                          <p className="text-muted-foreground mb-1">Issue Type</p>
                          <p className="font-medium capitalize">{order.issue_type}</p>
                        </div>
                        
                        <div className="p-2 rounded-lg bg-muted/50">
                          <p className="text-muted-foreground mb-1">Reported By</p>
                          <p className="font-medium truncate">{order.reported_by}</p>
                        </div>
                      </div>

                      {order.assigned_to && (
                        <div className="p-2 rounded-lg border bg-primary/5">
                          <p className="text-xs text-muted-foreground mb-1">Assigned To:</p>
                          <p className="text-sm font-medium">{order.assigned_to}</p>
                        </div>
                      )}

                      {order.resolution && (
                        <div className="p-2 rounded-lg border bg-[hsl(var(--status-clean))]/10">
                          <p className="text-xs text-muted-foreground mb-1">Resolution:</p>
                          <p className="text-sm">{order.resolution}</p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="pt-2 space-y-2">
                        {order.status === 'pending' && (
                          <Button
                            size="sm"
                            className="w-full"
                            onClick={() => updateWorkOrder(order.work_order_id, {
                              status: 'in_progress',
                              assigned_to: 'Current Staff'
                            })}
                            data-testid={`start-work-${order.work_order_id}`}
                          >
                            <Wrench className="w-4 h-4 mr-2" />
                            Start Work
                          </Button>
                        )}

                        {order.status === 'in_progress' && (
                          <Button
                            size="sm"
                            className="w-full"
                            onClick={() => updateWorkOrder(order.work_order_id, {
                              status: 'completed',
                              resolution: 'Issue resolved',
                              time_spent_minutes: 30
                            })}
                            data-testid={`complete-work-${order.work_order_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Mark Complete
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Marina Chat */}
      {showMarina && (
        <MarinaChat 
          department="maintenance"
          onClose={() => setShowMarina(false)}
        />
      )}
    </div>
  );
}

// ─── UTILITY FUNCTIONS ───

function formatEventType(eventType) {
  return eventType
    .split('.')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' › ');
}

function renderEventSummary(event) {
  const payload = event.payload;
  
  if (event.event_type === 'ops.maintenance_work_order_created') {
    return `${payload.location || payload.room_number}: ${payload.title} (${payload.priority} priority)`;
  }
  
  return JSON.stringify(payload);
}
